<template>
    <div class="page" :class="{'surface fill-height': phoneOnly}">
        <v-container :class="{'px-2': phoneOnly}">
            <div class="px-4 px-md-0 mb-4">
                <h1 class="text-h6 text-md-h5 font-weight-semibold strong-text--text mb-1">My Students</h1>
                <p class="details-text text--text mb-0">Students you are enrolled to teach, by course.</p>
            </div>

            <div v-if="loading && !enrollments" class="d-flex justify-center py-12">
                <v-progress-circular indeterminate color="primary" size="40" width="3" />
            </div>

            <v-list v-else-if="enrollments && enrollments.length" class="pa-md-0 pa-3 ma-0 transparent">
                <v-list-item
                    v-for="row in enrollments"
                    :key="row.id"
                    class="bordered rounded px-4 py-2 mb-3"
                    :class="{'px-3': phoneOnly}"
                    v-ripple="phoneOnly ? { class: 'ripple--color' } : false"
                    @click="openDetail(row)"
                >
                    <v-list-item-avatar :color="row.student && row.student.avatar ? '' : 'primary'" size="60" class="my-0 align-self-center rounded-lg">
                        <v-img v-if="row.student && row.student.avatar" :src="row.student.avatar" :alt="row.student.name" />
                        <span v-else class="text-h6 white--text font-weight-medium">{{ studentInitials(row) }}</span>
                    </v-list-item-avatar>
                    <v-list-item-content class="py-2">
                        <v-list-item-title class="d-flex align-center mb-1 pb-0" style="line-height: 1.2!important;">
                            <div class="font-weight-semibold strong-text--text text-wrap ">
                                {{ row.student ? row.student.name : '—' }}
                            </div>
                            <div class="ms-1 d-flex flex-wrap align-center" style="gap: 4px;">
                                <v-chip
                                    v-if="row.status"
                                    x-small
                                    label
                                    dark
                                    class="font-weight-medium px-2"
                                    :color="handleStatus(row.status).color"
                                >
                                    {{ handleStatus(row.status === 'approved' ? 'active' : row.status).text }}
                                </v-chip>
                                <!-- <v-chip
                                    v-if="row.finished"
                                    x-small
                                    label
                                    class="font-weight-medium px-2"
                                    color="grey darken-2"
                                    text-color="white"
                                >
                                    Finished
                                </v-chip> -->
                            </div>
                        </v-list-item-title>
                        <v-list-item-subtitle class="details-text text--text">
                            <span class="d-flex align-center">
                                <!-- <v-icon size="16" class="me-1 muted--text flex-shrink-0">mdi-book-open-variant</v-icon> -->
                                <span class="text-truncate">{{ row.course ? row.course.name : '—' }}</span>
                            </span>
                        </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-icon class="align-self-center my-0">
                        <v-icon color="muted">mdi-chevron-right</v-icon>
                    </v-list-item-icon>
                </v-list-item>
            </v-list>

            <v-alert v-else-if="!loading" type="info" color="muted" text class="empty-state-alert">
                <span class="medium-text">No students enrolled yet.</span>
            </v-alert>

            <div v-if="nextUrl" class="mt-2 px-4 px-md-0">
                <v-btn
                    block
                    text
                    outlined
                    color="primary"
                    class="grey lighten-3"
                    :loading="loadingMore"
                    :disabled="loadingMore"
                    @click="loadMore"
                >
                    Load more
                </v-btn>
            </div>
        </v-container>

        <v-dialog
            v-model="detailDialog"
            :fullscreen="phoneOnly"
            :max-width="phoneOnly ? undefined : 520"
            transition="slide-y-transition"
            scrollable
        >
            <v-card v-if="selected" class="overflow-hidden" :class="{'fill-height': phoneOnly}" :style="iosSafeArea ? 'padding-top: 45px!important;' : ''">
                <v-toolbar flat dense class="px-2">
                    <v-btn icon aria-label="Close" @click="detailDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title class="text-body-1 font-weight-semibold">{{ selected.student ? selected.student.name : '—' }}</v-toolbar-title>
                    <v-spacer />
                </v-toolbar>
                <v-divider />

                <v-card-text class="px-4 pt-6 pb-8">
                    <div class="text-center mb-6 px-2 dialog-hero">
                        <v-avatar :color="selected.student && selected.student.avatar ? '' : 'primary'" size="88" class="rounded-lg mb-3">
                            <v-img v-if="selected.student && selected.student.avatar" :src="selected.student.avatar" :alt="selected.student.name" />
                            <span v-else class="text-h4 white--text font-weight-medium">{{ studentInitials(selected) }}</span>
                        </v-avatar>
                        <div class="text-h6 font-weight-semibold strong-text--text">{{ selected.student ? selected.student.name : '—' }}</div>
                        <template v-if="selected.student">
                            <div
                                v-if="profileLineVisible(selected.student)"
                                class="hero-subtitle hero-profile-line text--text d-flex justify-center align-center"
                            >
                                <span
                                    v-if="selected.student.age != null"
                                    class="d-inline-flex align-center mx-1"
                                >
                                    <v-icon size="16" class="me-0 flex-shrink-0" color="muted">mdi-circle-medium</v-icon>
                                    <span>{{ selected.student.age }} years old</span>
                                </span>
                                <span
                                    v-if="selected.student.gender"
                                    class="d-inline-flex align-center mx-1"
                                >
                                    <v-icon size="16" class="me-0 flex-shrink-0" color="muted">mdi-circle-medium</v-icon>
                                    <span>{{ selected.student.gender }}</span>
                                </span>
                                <span
                                    v-if="studentCountryLabel(selected.student)"
                                    class="d-inline-flex align-center mx-1"
                                >
                                    <v-icon size="14" class="me-1 flex-shrink-0">mdi-earth</v-icon>
                                    <span>{{ studentCountryLabel(selected.student) }}</span>
                                </span>
                            </div>
                            <div
                                v-if="selected.student.timezone"
                                class="hero-subtitle hero-timezone muted--text"
                            >

                                <div class="d-flex align-center justify-center">
                                    {{ selected.student.timezone }} - 
                                    It's
                                    <div class="text--text mx-1">
                                        <timezone-clock :key="studentClockKey" :timezone="selected.student.timezone" />
                                    </div>
                                    now for {{ selected.student.name }}
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- <h3 class="text-overline muted--text letter-spacing-1 mb-2">Enrollment</h3> -->
                    <v-sheet rounded class="pa-3 mb-6 bordered" outlined>
                        <div class="detail-row" v-if="selected.course">
                            <span class="detail-label muted--text">Course</span>
                            <span class="strong-text--text font-weight-medium">{{ selected.course.name }}</span>
                        </div>
                        <div class="detail-row" v-if="selected.status">
                            <span class="detail-label muted--text">Status</span>
                            <v-chip x-small label dark class="font-weight-medium" :color="handleStatus(selected.status).color">
                                {{ handleStatus(selected.status === 'approved' ? 'active' : selected.status).text }}
                            </v-chip>
                        </div>
                        <div class="detail-row" v-if="selected.course">
                            <span class="detail-label muted--text">Academic Advisor</span>
                            <span class="strong-text--text font-weight-medium">{{ selected.student.academic_advisor }}</span>
                        </div>
                        <div class="detail-row" v-if="selected.course">
                            <span class="detail-label muted--text">Languages</span>
                            <span class="strong-text--text font-weight-medium">{{ selected.student.languages }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label muted--text">Enrolled on</span>
                            <div class="text-end">
                                <div class="strong-text--text">{{ dateTimeFormat(selected.created_at, true).split(' ')[0] || '—' }}</div>
                                <div v-if="selected.created_ago" class="details-text muted--text">{{ selected.created_ago }}</div>
                            </div>
                        </div>
                        <div v-if="selected.finished_at_datetime || selected.finished_at_ago" class="detail-row">
                            <span class="detail-label muted--text">Completed</span>
                            <div class="text-end">
                                <div class="strong-text--text">{{ selected.finished_at_datetime || '—' }}</div>
                                <div v-if="selected.finished_at_ago" class="details-text muted--text">{{ selected.finished_at_ago }}</div>
                            </div>
                        </div>
                    </v-sheet>

                    <template v-if="selected.student && selected.student.evaluation_summary">
                        <h3 class="text-overline muted--text letter-spacing-1 mb-2">Evaluation</h3>
                        <v-sheet rounded class="pa-4 mb-6 bordered" outlined>
                            <p class="strong-text--text mb-0" style="white-space: pre-line; line-height: 1.55rem;">{{ selected.student.evaluation_summary }}</p>
                        </v-sheet>
                    </template>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import TimezoneClock from '@/components/default/timezone-clock'


export default {
    name: 'MyStudents',
    components: {
        TimezoneClock,
    },
    data() {
        return {
            enrollments: null,
            nextUrl: null,
            loading: false,
            loadingMore: false,
            scrollHandler: null,
            detailDialog: false,
            selected: null,
        }
    },
    computed: {
        ...GlobalComputed,
        studentClockKey() {
            if (!this.selected) return '';
            const sid = this.selected.student && this.selected.student.id;
            return sid != null ? `s-${sid}` : `e-${this.selected.id}`;
        },
    },
    watch: {
        loading(loading) {
            this.setLinearLoading(loading && !this.enrollments);
        },
    },
    methods: {
        openDetail(row) {
            this.selected = row;
            this.detailDialog = true;
        },
        profileLineVisible(student) {
            if (!student) return false;
            return !!(
                this.studentCountryLabel(student) ||
                student.gender ||
                student.age != null
            );
        },
        studentCountryLabel(student) {
            if (!student) return '';
            return student.country_name || student.country || '';
        },
        studentInitials(row) {
            const name = row.student && row.student.name ? row.student.name : '?';
            const parts = name.trim().split(/\s+/);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.slice(0, 2).toUpperCase();
        },
        fetchPage(url, append) {
            return window.axios.get(url).then((response) => {
                const data = response.data.data || [];
                const next = response.data.links ? response.data.links.next : null;
                if (append && this.enrollments) {
                    this.enrollments.push(...data);
                } else {
                    this.enrollments = data;
                }
                this.nextUrl = next;
            });
        },
        loadInitial() {
            if (this.loading) return;
            this.loading = true;
            this.fetchPage('/teacher/my-enrollments', false)
                .catch(() => {
                    this.showMsg('Error occurred while fetching students', 'error');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadMore() {
            if (!this.nextUrl || this.loadingMore || this.loading) return;
            this.loadingMore = true;
            this.fetchPage(this.nextUrl, true)
                .catch(() => {
                    this.showMsg('Error occurred while loading more', 'error');
                })
                .finally(() => {
                    this.loadingMore = false;
                });
        },
        onScroll() {
            if (!this.nextUrl || this.loadingMore || this.loading) return;
            const visible = document.documentElement.clientHeight;
            const pageHeight = document.documentElement.scrollHeight;
            const scrollY = window.scrollY || window.pageYOffset;
            if (visible + scrollY >= pageHeight - 140) {
                this.loadMore();
            }
        },
        ...GlobalMethods,
    },
    mounted() {
        this.loadInitial();
        this.scrollHandler = () => this.onScroll();
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
    },
    beforeDestroy() {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    },
};
</script>

<style scoped>
.min-width-0 {
    min-width: 0;
}
.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.detail-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
}
.detail-row:first-of-type {
    padding-top: 0;
}
.detail-label {
    flex-shrink: 0;
    max-width: 48%;
    display: flex;
    align-items: center;
    font-size: 13px;
}
.theme--dark .detail-row {
    border-bottom-color: rgba(255, 255, 255, 0.08);
}
.dialog-hero .hero-subtitle {
    line-height: 1.45;
    font-size: 13px!important;
}
.dialog-hero .hero-profile-line {
    margin-top: 10px;
    max-width: 100%;
}
.dialog-hero .hero-timezone {
    margin-top: 10px;
}
/* First row after name when there is no course */
.dialog-hero .text-h6 + .hero-profile-line,
.dialog-hero .text-h6 + .hero-timezone {
    margin-top: 6px;
}
/* First row after course line */
.dialog-hero .details-text.text--text + .hero-profile-line,
.dialog-hero .details-text.text--text + .hero-timezone {
    margin-top: 10px;
}
/* Timezone block below profile row */
.dialog-hero .hero-profile-line + .hero-timezone {
    margin-top: 6px;
}
</style>
